import {AmountSpinnerHandler} from "./Handler";
import {ProductScraper} from "../Product/ProductScraper";
import {renderToString} from "react-dom/server";
import React from "react";
import {IProps, MinimumAmountReached} from "../Product/LightBoxes/MinimumAmountReached";
import {MaximumAmountReached} from "../Product/LightBoxes/MaximumAmountReached";

export class AmountSpinnerRegister {

    private readonly _register: Map<HTMLElement, AmountSpinnerHandler>;
    private readonly _entryElement: HTMLElement;

    public constructor(entryElement: HTMLElement) {
        this._register = new Map();
        this._entryElement = entryElement;
    }

    public registerExistingAndWatchForNew(): void {
        this.registerExistingSpinners();
        this.watchForNewSpinners();
    }

    private registerExistingSpinners(): void {

        let spinnerElementList = this._entryElement.querySelectorAll<HTMLElement>(".amount-spinner");

        spinnerElementList.forEach((spinnerElement) => {
            this.registerSpinnerElement(spinnerElement);
        });
    }

    private watchForNewSpinners() : void {

        this._entryElement.addEventListener("click", (event) => {

            let spinnerElement = AmountSpinnerRegister.extractSpinnerElementFromEventTarget(event);

            if (spinnerElement === null) {
                return;
            }

            if (this.isSpinnerElementRegistered(spinnerElement)) {
                return;
            }

            let handler = this.registerSpinnerElement(spinnerElement);

            if (AmountSpinnerRegister.shouldTriggerSubtractEvent(event)) {
                handler.triggerSubtractEvent();

            } else if (AmountSpinnerRegister.shouldTriggerAddEvent(event)) {
                handler.triggerAddEvent();
            }
        });
    }

    private static extractSpinnerElementFromEventTarget(event : MouseEvent) : HTMLElement {

        let interactedElement = <HTMLElement>event.target;
        let isSpinnerElement = interactedElement.classList.contains("amount-spinner");
        let isComponentOfSpinnerElement = interactedElement.closest(".amount-spinner") !== null;

        let spinnerElement: HTMLElement = null;

        if (isSpinnerElement) {
            spinnerElement = interactedElement;

        } else if (isComponentOfSpinnerElement) {
            spinnerElement = interactedElement.closest(".amount-spinner");
        }

        return spinnerElement
    }

    private isSpinnerElementRegistered(spinnerElement: HTMLElement): boolean {
        return this._register.has(spinnerElement);
    }

    public registerSpinnerElement(spinnerElement : HTMLElement): AmountSpinnerHandler {

        if (this.isSpinnerElementRegistered(spinnerElement)) {
            return null;
        }

        let productElement = spinnerElement.closest<HTMLElement>(".product-detail, .product-tile, .product");
        let productScraper = new ProductScraper(productElement);

        let product = productScraper.scrape();

        let handler = new AmountSpinnerHandler(
            spinnerElement,
            product.measurementUnit,
            product.pieceAmount,
            product.minimumSaleAmount,
            product.maximumSaleAmount
        );

        this._register.set(spinnerElement, handler);

        handler.onMinimumReached = () => {

            const lightBoxName = "lb-product-minimum-amount-reached";

            let lightBoxElement = document.getElementById(lightBoxName);
            if (lightBoxElement !== null) {
                lightBoxElement.remove();
            }

            let lightBoxHTML = renderToString(React.createElement<IProps>(MinimumAmountReached, {product : product}));
            document.getElementsByTagName("body")[0].insertAdjacentHTML("beforeend", lightBoxHTML);

            window.showLightBox(lightBoxName);
        };

        handler.onMaximumReached = () => {

            const lightBoxName = "lb-product-maximum-amount-reached";

            let lightBoxElement = document.getElementById(lightBoxName);
            if (lightBoxElement !== null) {
                lightBoxElement.remove();
            }

            let lightBoxHTML = renderToString(React.createElement<IProps>(MaximumAmountReached, {product : product}));
            document.getElementsByTagName("body")[0].insertAdjacentHTML("beforeend", lightBoxHTML);

            window.showLightBox(lightBoxName);
        };

        handler.handle();

        return handler;
    }

    private static shouldTriggerSubtractEvent(event: MouseEvent): boolean {

        let interactedElement = <HTMLElement>event.target;
        let isButton = interactedElement.tagName === "button" && interactedElement.classList.contains("minus");
        let isComponentOfButton = interactedElement.closest("button.minus") !== null;

        return isButton || isComponentOfButton;
    }

    private static shouldTriggerAddEvent(event: MouseEvent): boolean {

        let interactedElement = <HTMLElement>event.target;
        let isButton = interactedElement.tagName === "button" && interactedElement.classList.contains("plus");
        let isComponentOfButton = interactedElement.closest("button.plus") !== null;

        return isButton || isComponentOfButton;
    }
}